// Wallet adapter for x402 USDC payments — wagmi-based.
//
// Replaces the old window.ethereum-direct flow. Now sources accounts/chain/connectors from
// wagmi (so all installed extensions appear via EIP-6963 + Coinbase Wallet + WalletConnect),
// and obtains a viem WalletClient via wagmi's `getConnectorClient` action when we need to
// build the x402 paid-fetch.
//
// Surface kept compatible with the previous module:
//   - useWallet()  : React hook returning state + connect/disconnect/openPicker handlers
//   - connectors   : list of available connector entries for picker UI
//   - x402Checkout : low-level call used by services.js (window.XWallet bridge)
//   - signMessage  : SIWE-style personal_sign helper
//   - XWallet      : grouped export, also pinned to window.XWallet for legacy callers
import React from 'react';
import {
  useAccount, useConnect, useDisconnect, useChainId, useSwitchChain,
  useSignMessage, useConnectors,
} from 'wagmi';
import {
  switchChain as switchChainAction,
  signMessage as signMessageAction,
  signTypedData as signTypedDataAction,
  getAccount as getAccountAction,
  readContract as readContractAction,
  writeContract as writeContractAction,
  waitForTransactionReceipt as waitForTransactionReceiptAction,
} from 'wagmi/actions';
import { x402Client } from '@x402/core/client';
import { decodePaymentResponseHeader } from '@x402/core/http';
import { ExactEvmScheme } from '@x402/evm/exact/client';
import { wrapFetchWithPayment } from '@x402/fetch';
import { erc20Abi, getAddress } from 'viem';
import { wagmiConfig } from './wagmi-config.js';
import { XApi } from './services.js';

const TARGET = (opts = {}) => {
  const cfg = (XApi && XApi.config) || {};
  if (opts.provider === 'b402') {
    return { chainId: cfg.b402ChainId || 56, network: cfg.b402Network || 'eip155:56' };
  }
  return { chainId: cfg.x402ChainId || 8453, network: cfg.x402Network || 'eip155:8453' };
};

const PERMIT2_ADDRESS = '0x000000000022D473030F116dDEE9F6B43aC78BA3';
const B402_ASSETS = {
  U: {
    address: '0xcE24439F2d9C6a2289F741120FE202248B666666',
    method: 'eip3009',
    decimals: 18,
  },
  USD1: {
    address: '0x8d0D000Ee44948FC98c9B98A4FA4921476f08B0d',
    method: 'eip3009',
    decimals: 18,
  },
  USDT: {
    address: '0x55d398326f99059fF775485246999027B3197955',
    method: 'permit2-exact',
    decimals: 18,
  },
  USDC: {
    address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
    method: 'permit2-exact',
    decimals: 18,
  },
};

const MIN_PERMIT2_APPROVAL_TOKENS = 10n;

const chainIdFromNetwork = (network) => Number(String(network || '').split(':')[1] || 0);

const randomUint256 = () => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return BigInt('0x' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')).toString();
};

const normalizeB402Asset = (asset) => {
  const key = String(asset || 'U').toUpperCase();
  return B402_ASSETS[key] ? key : 'U';
};

const addressEq = (a, b) => {
  try { return getAddress(a) === getAddress(b); } catch { return String(a || '').toLowerCase() === String(b || '').toLowerCase(); }
};

const permit2ApprovalFloor = (assetAddress) => {
  const token = Object.values(B402_ASSETS).find(asset => addressEq(asset.address, assetAddress));
  const decimals = BigInt(token?.decimals ?? 18);
  return MIN_PERMIT2_APPROVAL_TOKENS * (10n ** decimals);
};

const selectB402Requirements = (asset, network) => (_version, accepts) => {
  const symbol = normalizeB402Asset(asset);
  const pref = B402_ASSETS[symbol];
  const wanted = accepts.find(r =>
    r.network === network &&
    r.scheme === 'exact' &&
    addressEq(r.asset, pref.address) &&
    (r.extra || {}).assetTransferMethod === pref.method
  );
  if (wanted) return wanted;

  const byAsset = accepts.find(r => r.network === network && addressEq(r.asset, pref.address));
  if (byAsset) return byAsset;

  return accepts[0];
};

// Cache the paidFetch keyed by address — building it requires a wallet client + signer
// registration, so we don't want to redo that on every checkout call.
let paidFetchCache = { key: null, paidFetch: null };

const buildSigner = (address) => ({
  address,
  signTypedData: async (args) => {
    try {
      return await signTypedDataAction(wagmiConfig, {
        account: address,
        domain: args.domain,
        types: args.types,
        primaryType: args.primaryType,
        message: args.message,
      });
    } catch (err) {
      throw new Error(err && err.message ? err.message : 'Signature rejected');
    }
  },
});

const ensurePermit2Approval = async (requirements, owner) => {
  const chainId = chainIdFromNetwork(requirements.network);
  const token = getAddress(requirements.asset);
  const allowance = await readContractAction(wagmiConfig, {
    chainId,
    address: token,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [getAddress(owner), PERMIT2_ADDRESS],
  });
  const currentAllowance = BigInt(allowance?.toString?.() || allowance || 0);
  const requestedAllowance = BigInt(requirements.amount);
  const minAllowance = permit2ApprovalFloor(requirements.asset);
  const requiredAllowance = requestedAllowance > minAllowance ? requestedAllowance : minAllowance;
  if (currentAllowance >= requiredAllowance) return;

  const approve = async (amount) => {
    const hash = await writeContractAction(wagmiConfig, {
      chainId,
      account: owner,
      address: token,
      abi: erc20Abi,
      functionName: 'approve',
      args: [PERMIT2_ADDRESS, amount],
    });
    await waitForTransactionReceiptAction(wagmiConfig, { chainId, hash });
  };

  if (currentAllowance > 0n) await approve(0n);
  await approve(requiredAllowance);
};

class B402ExactEvmScheme {
  scheme = 'exact';

  constructor(signer) {
    this.signer = signer;
    this.exact = new ExactEvmScheme(signer);
  }

  async createPaymentPayload(x402Version, requirements) {
    const method = (requirements.extra || {}).assetTransferMethod;
    if (method !== 'permit2-exact') {
      return this.exact.createPaymentPayload(x402Version, requirements);
    }
    return {
      x402Version,
      payload: await this.createPermit2ExactPayload(requirements),
    };
  }

  async createPermit2ExactPayload(requirements) {
    const spender = (requirements.extra || {}).spenderAddress;
    if (!spender) throw new Error('B402 Permit2 spender missing. Refresh supported configuration on the backend.');

    await ensurePermit2Approval(requirements, this.signer.address);

    const chainId = chainIdFromNetwork(requirements.network);
    const nowSec = Math.floor(Date.now() / 1000);
    const nonce = randomUint256();
    const deadline = String(nowSec + 3600);
    const validAfter = String(Math.max(0, nowSec - 60));

    const domain = {
      name: 'Permit2',
      chainId,
      verifyingContract: PERMIT2_ADDRESS,
    };
    const types = {
      PermitWitnessTransferFrom: [
        { name: 'permitted', type: 'TokenPermissions' },
        { name: 'spender', type: 'address' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' },
        { name: 'witness', type: 'Witness' },
      ],
      TokenPermissions: [
        { name: 'token', type: 'address' },
        { name: 'amount', type: 'uint256' },
      ],
      Witness: [
        { name: 'to', type: 'address' },
        { name: 'validAfter', type: 'uint256' },
      ],
    };
    const message = {
      permitted: {
        token: getAddress(requirements.asset),
        amount: BigInt(requirements.amount),
      },
      spender: getAddress(spender),
      nonce: BigInt(nonce),
      deadline: BigInt(deadline),
      witness: {
        to: getAddress(requirements.payTo),
        validAfter: BigInt(validAfter),
      },
    };

    const signature = await this.signer.signTypedData({
      domain,
      types,
      primaryType: 'PermitWitnessTransferFrom',
      message,
    });

    return {
      signature,
      permit2Authorization: {
        permitted: {
          token: requirements.asset,
          amount: requirements.amount,
        },
        from: this.signer.address,
        spender,
        nonce,
        deadline,
        witness: {
          to: requirements.payTo,
          validAfter,
        },
      },
    };
  }
}

const buildPaidFetch = (address, payload) => {
  const provider = payload.provider === 'b402' ? 'b402' : 'x402';
  const asset = provider === 'b402' ? normalizeB402Asset(payload.asset) : '';
  const key = `${address}:${provider}:${payload.network}:${asset}`;
  if (paidFetchCache.key === key && paidFetchCache.paidFetch) return paidFetchCache.paidFetch;
  // The x402 EVM signer wants an object with { address, signTypedData(args) }. We delegate
  // to wagmi's signTypedData action, which routes through whichever connector is currently
  // active (injected MetaMask, OKX, Coinbase Wallet, WalletConnect, …) — no need to grab
  // window.ethereum or build a viem WalletClient ourselves.
  const signer = buildSigner(address);
  const x402 = new x402Client(provider === 'b402' ? selectB402Requirements(asset, payload.network) : undefined);
  x402.register(payload.network, provider === 'b402' ? new B402ExactEvmScheme(signer) : new ExactEvmScheme(signer));
  const paidFetch = wrapFetchWithPayment(window.fetch.bind(window), x402);
  paidFetchCache = { key, paidFetch };
  return paidFetch;
};

// payload: { url, amountUsd, accessToken, provider, asset, network, chainId, stablecoin }
export const x402Checkout = async (payload) => {
  const account = getAccountAction(wagmiConfig);
  const address = account && account.address;
  if (!address) throw new Error('Wallet not connected');

  // Ensure we're on the target chain (Base by default). Use the public getAccount action
  // (account.chainId reflects the wallet's currently selected chain).
  const target = { chainId: payload.chainId, network: payload.network };
  if (account.chainId !== target.chainId) {
    try { await switchChainAction(wagmiConfig, { chainId: target.chainId }); }
    catch (err) {
      const chainName = target.chainId === 56 ? 'BSC' : target.chainId === 8453 ? 'Base' : `chain ${target.chainId}`;
      throw new Error(`Switch to ${chainName} and try again.`);
    }
  }

  const paidFetch = buildPaidFetch(address, payload);
  const headers = { 'Content-Type': 'application/json' };
  if (payload.accessToken) headers.Authorization = 'Bearer ' + payload.accessToken;
  const resp = await paidFetch(payload.url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      amountUsd: payload.amountUsd,
      ...(payload.provider ? { provider: payload.provider } : {}),
      ...(payload.asset ? { asset: payload.asset } : {}),
    }),
  });
  const ct = resp.headers.get('content-type') || '';
  const data = ct.includes('application/json') ? await resp.json() : await resp.text();

  let receipt = null;
  try {
    const hdr = resp.headers.get('PAYMENT-RESPONSE') || resp.headers.get('payment-response');
    if (hdr) receipt = decodePaymentResponseHeader(hdr);
  } catch {}
  return { status: resp.status, data, receipt };
};

// SIWE-style personal_sign. Caller must already have a connected wallet — we no longer
// auto-prompt connection here (use the picker explicitly).
export const signMessageWithWallet = async (message) => {
  const account = getAccountAction(wagmiConfig);
  const address = account && account.address;
  if (!address) throw new Error('Wallet not connected');
  const signature = await signMessageAction(wagmiConfig, { account: address, message });
  return { address, signature };
};

// Friendly metadata for connector buttons in the picker. Maps connector .id (or .name)
// to a label + icon hint so the UI stays consistent regardless of which wallets are installed.
const CONNECTOR_META = {
  metaMaskSDK:    { label: 'MetaMask',         hint: 'Browser extension' },
  metaMask:       { label: 'MetaMask',         hint: 'Browser extension' },
  io_metamask:    { label: 'MetaMask',         hint: 'Browser extension' },
  coinbaseWalletSDK: { label: 'Coinbase Wallet', hint: 'Extension or mobile' },
  coinbaseWallet: { label: 'Coinbase Wallet',  hint: 'Extension or mobile' },
  walletConnect:  { label: 'WalletConnect',    hint: 'Scan QR with mobile' },
  injected:       { label: 'Browser wallet',   hint: 'Other injected extension' },
  okxWallet:      { label: 'OKX Wallet',       hint: 'Browser extension' },
  com_okex_wallet:{ label: 'OKX Wallet',       hint: 'Browser extension' },
  rabby:          { label: 'Rabby',            hint: 'Browser extension' },
  io_rabby:       { label: 'Rabby',            hint: 'Browser extension' },
  phantom:        { label: 'Phantom',          hint: 'Browser extension' },
  app_phantom:    { label: 'Phantom',          hint: 'Browser extension' },
};

const labelForConnector = (c) => {
  if (!c) return { label: 'Unknown', hint: '' };
  // EIP-6963 connectors expose the wallet's RDNS as id (e.g. "io.metamask"); we normalise
  // to underscored form for the lookup table, then fall back to connector.name.
  const norm = (c.id || '').replace(/\./g, '_');
  const meta = CONNECTOR_META[norm] || CONNECTOR_META[c.id] || CONNECTOR_META[c.type];
  if (meta) return meta;
  return { label: c.name || c.id || 'Wallet', hint: '' };
};

// React hook — single shape used by TopUp and any future caller. Wraps wagmi's hooks so
// callers don't need to import from wagmi directly (keeps the surface centralised here).
export const useWallet = () => {
  const { address, isConnected, isConnecting, isReconnecting } = useAccount();
  const chainId = useChainId();
  const { connectors: rawConnectors, connectAsync, isPending: isConnectingAsync, error: connectError } = useConnect();
  const allConnectors = useConnectors();
  const { disconnectAsync } = useDisconnect();
  const { switchChainAsync } = useSwitchChain();
  const { signMessageAsync } = useSignMessage();

  // De-duplicate by id. Wagmi sometimes lists the same wallet twice (once via factory,
  // once via EIP-6963 announcement). Prefer the EIP-6963 entry (has rdns/icon).
  const connectors = React.useMemo(() => {
    const seen = new Map();
    for (const c of allConnectors) {
      const key = (c.id || c.name || '').toLowerCase();
      if (!seen.has(key) || c.icon) seen.set(key, c);
    }
    return Array.from(seen.values()).map(c => ({
      raw: c,
      id: c.id,
      name: c.name,
      icon: c.icon,        // base64 or URL — wagmi populates via EIP-6963
      ...labelForConnector(c),
    }));
  }, [allConnectors]);

  const connect = React.useCallback(async (connectorOrId) => {
    let target = null;
    if (!connectorOrId) target = allConnectors[0];
    else if (typeof connectorOrId === 'string') target = allConnectors.find(c => c.id === connectorOrId);
    else if (connectorOrId.raw) target = connectorOrId.raw;
    else target = connectorOrId;
    if (!target) throw new Error('No wallet connector available');
    // Connect WITHOUT chainId. Some connectors (notably OKX) silently fail when asked to
    // pre-switch to a chain they're not currently on — the wallet pop-up never appears.
    // xapi-new also calls connectAsync({ connector }) only, then switches chain afterwards
    // via useSwitchChain. We mirror that pattern here.
    return connectAsync({ connector: target });
  }, [allConnectors, connectAsync]);

  const disconnect = React.useCallback(async () => {
    paidFetchCache = { key: null, paidFetch: null };
    return disconnectAsync();
  }, [disconnectAsync]);

  const switchToTarget = React.useCallback(async () => {
    const t = TARGET();
    if (chainId === t.chainId) return;
    return switchChainAsync({ chainId: t.chainId });
  }, [chainId, switchChainAsync]);

  return {
    address: address || null,
    chainId: chainId || null,
    connecting: isConnecting || isReconnecting || isConnectingAsync,
    connectError: connectError ? (connectError.message || 'Connect failed') : null,
    // hasProvider stays for backwards-compat with TopUpPage, but it's now always true
    // because wagmi always exposes at least the WalletConnect / coinbase fallbacks even
    // without a browser extension.
    hasProvider: connectors.length > 0,
    connectors,
    connect,
    disconnect,
    signMessage: (msg) => signMessageAsync({ message: msg, account: address }).then(sig => ({ address, signature: sig })),
    switchToTarget,
  };
};

// Export an XWallet object so existing callers (services.js x402 path, TopUpPage legacy) keep
// working. The window.XWallet bridge stays for the `XApi.x402Service.checkout` call which
// runs outside React-component context.
export const XWallet = {
  useWallet,
  x402Checkout,
  signMessage: signMessageWithWallet,
  // Kept for compatibility but largely unused now (wagmi handles chain switching internally).
  ensureChain: (chainId) => switchChainAction(wagmiConfig, { chainId }),
};

if (typeof window !== 'undefined') window.XWallet = XWallet;
